/* eslint-disable max-lines-per-function */
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles, styled} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AccountCircle from '@material-ui/icons/AccountCircle';
import AppDownloadIcon from '@material-ui/icons/GetApp';
import AppManualIcon from '@material-ui/icons/OndemandVideo';
import {PropTypes} from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Link as RouterLink} from 'react-router-dom';
import locale from '../locale/Localization';
import {addAppLicense, getAppLicense} from '../script/apps';
import {NAV_SELECT_PROFILE} from '../utils/constants';

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state) => {
  return {
    user: state.user
  };
};

const useStyles = makeStyles(() => ({
  root: {flexGrow: 1},
  headline: {paddingTop: '3%', paddingBottom: '1%', color: 'white'},
  button: {marginRight: '1em'},
  checkbox: {marginTop: '-3%'}
}));

const Item = styled(Paper)(({theme, padding, elevation, textalign}) => ({
  ...theme.typography.body2,
  padding: theme.spacing(padding),
  elevation: elevation,
  textAlign: textalign
}));

const APP_NAME_MINI_SIGNER = 'minisigner+';
const APP_NAME_SIGNER = 'signer';
const URL_MINI_SIGNER_MAC = '/mini-signer/MiniSigner+.dmg';
const URL_MINI_SIGNER_WIN = '/mini-signer/MiniSigner+ Setup.exe';
const URL_SIGNER_MAC = '/static/signer/eMescad-Signer.dmg';
const URL_SIGNER_WIN = '/static/signer/eMescad-Signer-Setup.exe';
const URL_SIGNER_EULA_DE = '/static/signer/eMescad-Signer_EULA_DE.pdf';
const URL_SIGNER_EULA_FR = '/static/signer/eMescad-Signer_EULA_FR.pdf';
const URL_SIGNER_MANUAL_DE = 'https://youtu.be/fLiirsMJ4lo';
const URL_SIGNER_MANUAL_FR = 'https://youtu.be/zF6UA7TSdyU';

/**
 * The signer information and download view.
 *
 * @param {P} props - The property data.
 * @return {string} - The component contents to render.
 */
function Signer(props) {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [userHasSignerLicense, setUserHasSignerLicense] = useState(false);
  const [userHasMiniSignerLicense, setUserHasMiniSignerLicense] =
    useState(false);

  const lang = locale.getLanguage();
  const langIsFrench = lang === 'fr';
  const eulaURL = langIsFrench ? URL_SIGNER_EULA_FR : URL_SIGNER_EULA_DE;
  const manualURL = langIsFrench ? URL_SIGNER_MANUAL_FR : URL_SIGNER_MANUAL_DE;

  const personHasContactInformation = props.user.contactInformation
    ? true
    : false;
  let disableDownload = true;
  if (personHasContactInformation) {
    disableDownload = props.user.contactInformation.mobileNumber ? false : true;
  }

  const showManualButton = disableDownload || userHasSignerLicense;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setChecked(false);
  };

  const handleDownload = (url) => {
    window.open(url);
  };

  const handleCheckboxChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleSubmit = () => {
    addAppLicense(APP_NAME_SIGNER).then(() => {
      setUserHasSignerLicense(true);
    });
  };

  const fetchAppUsageRights = () => {
    getAppLicense(APP_NAME_SIGNER).then((response) => {
      setUserHasSignerLicense(response);
    });
    getAppLicense(APP_NAME_MINI_SIGNER).then((response) => {
      setUserHasMiniSignerLicense(response);
    });
  };

  const renderSignerDownload = (urlMac, urlWin) => {
    return (
      <ButtonGroup disableElevation variant='outlined' color='primary'>
        <Tooltip
          placement='top'
          title={locale.signerDownloadDialogButtonMacTooltip}
        >
          <Button
            startIcon={<AppDownloadIcon />}
            onClick={() => handleDownload(urlMac)}
          >
            {locale.signerDownloadDialogButtonMac}
          </Button>
        </Tooltip>
        <Tooltip
          placement='top'
          title={locale.signerDownloadDialogButtonWindowsTooltip}
        >
          <Button
            startIcon={<AppDownloadIcon />}
            onClick={() => handleDownload(urlWin)}
          >
            {locale.signerDownloadDialogButtonWindows}
          </Button>
        </Tooltip>
      </ButtonGroup>
    );
  };

  const renderAppDownloadDialog = () => {
    return (
      <div>
        <Dialog
          disableBackdropClick
          aria-labelledby='MiniSigner+ Download'
          onClose={handleClose}
          open={open}
        >
          <DialogTitle
            id='minisigner-download-dialog-title'
            onClose={handleClose}
          >
            {locale.signerDownloadDialogTitel}
          </DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              {locale.signerDownloadDialogContent}
            </Typography>
            <Typography gutterBottom>
              {locale.signerDownloadDialogContent2}
            </Typography>
            <Typography gutterBottom>
              {' '}
              {locale.signerDownloadDialogContentEula}
            </Typography>
            <Button
              variant='text'
              color='primary'
              style={{marginBottom: '1em', textDecoration: 'underline'}}
              onClick={() => handleDownload(eulaURL)}
            >
              {locale.signerDownloadDialogEulaDownload}
            </Button>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    autoFocus
                    className={classes.checkbox}
                    checked={checked || userHasSignerLicense}
                    disabled={userHasSignerLicense}
                    color='primary'
                    onChange={handleCheckboxChange}
                  />
                }
                label={locale.signerDownloadDialogAgreement}
              />
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant='outlined'>
              {userHasSignerLicense
                ? locale.signerDownloadDialogButtonClose
                : locale.signerDownloadDialogButtonCancel}
            </Button>
            {!userHasSignerLicense && (
              <Button
                disabled={!checked}
                onClick={handleSubmit}
                color='primary'
                variant='contained'
              >
                {locale.signerDownloadDialogButtonSubmit}
              </Button>
            )}
            {userHasSignerLicense &&
              renderSignerDownload(URL_SIGNER_MAC, URL_SIGNER_WIN)}
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  const renderSignerCard = () => {
    return (
      <Paper elevation={24}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Item padding={2} elevation={1} textalign='left'>
              <Typography variant='h6' gutterBottom>
                {locale.signerHeadline}
              </Typography>
              <Typography variant='body1' gutterBottom>
                {locale.signerDescription}
              </Typography>
              <Typography variant='body1' gutterBottom>
                {locale.signerDescriptionDownload}
              </Typography>
              <Typography variant='body1'>
                {disableDownload ? locale.signerDescriptionMobileNumber : ''}
              </Typography>
            </Item>
          </Grid>
          <Grid item xs>
            <Item padding={1} elevation={0} textalign='right'>
              {showManualButton && (
                <Button
                  className={classes.button}
                  component={disableDownload ? RouterLink : Button}
                  to={disableDownload ? NAV_SELECT_PROFILE : ''}
                  onClick={() => {
                    disableDownload ? '' : window.open(manualURL, '_blank');
                  }}
                  color={disableDownload ? 'primary' : 'default'}
                  variant={disableDownload ? 'contained' : 'outlined'}
                  startIcon={
                    disableDownload ? <AccountCircle /> : <AppManualIcon />
                  }
                >
                  {disableDownload
                    ? locale.signerEditProfileButton
                    : locale.signerManualDownloadButton}
                </Button>
              )}
              <Button
                className={classes.button}
                disabled={disableDownload}
                onClick={handleClickOpen}
                color='primary'
                variant='contained'
                startIcon={<AppDownloadIcon />}
              >
                {locale.signerAppDownloadButton}
              </Button>
            </Item>
            {renderAppDownloadDialog()}
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderMiniSignerCard = () => {
    return (
      <Paper elevation={24}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Item padding={2} elevation={1} textalign='left'>
              <Typography variant='h6' gutterBottom>
                {locale.signerMiniHeadline}
              </Typography>
              <Typography variant='body1' gutterBottom>
                {locale.signerMiniDescription}
              </Typography>
              <Typography variant='body1' gutterBottom>
                {locale.signerMiniDescriptionDownload}
              </Typography>
              <Typography variant='body1'>
                {disableDownload
                  ? locale.signerMiniDescriptionMobileNumber
                  : ''}
              </Typography>
            </Item>
          </Grid>
          <Grid item xs>
            <Item padding={1} elevation={0} textalign='right'>
              {renderSignerDownload(URL_MINI_SIGNER_MAC, URL_MINI_SIGNER_WIN)}
            </Item>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  useEffect(() => {
    fetchAppUsageRights();
  }, []);

  return (
    <div className={classes.root}>
      <Typography variant='h5' gutterBottom className={classes.headline}>
        {locale.signerTitle}
      </Typography>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          {renderSignerCard()}
        </Grid>
        {userHasMiniSignerLicense && (
          <Grid item xs={12}>
            {renderMiniSignerCard()}
          </Grid>
        )}
      </Grid>
    </div>
  );
}

Signer.propTypes = {
  user: PropTypes.object.isRequired
};

export default connect(mapStateToProps, null)(Signer);
